import { FC, useState } from 'react'

import styled from 'styled-components'

import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { numberToMoney } from 'src/Constants/functions'
import { PrimaryInfoModal } from 'src/Components/PrimaryInfoModal'

import { PrimaryDottedButton } from '../Buttons/PrimaryDottedButton'

const MIN_CURATORS_THRESHOLD = 3

export interface ManualMatchingModalFooterProps {
  totalAmount: number
  totalPlaylists: number
  totalCurators: number
  isBelowMinBudget?: boolean
  minBudget?: number
  disabled?: boolean
  loading?: boolean
  onContinue: () => void
  onAddMoreCurators?: () => void
}

const Container = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 16px;
  background: ${({ theme }) => theme.color.white};
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  z-index: 100;
`
const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
`

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TotalLabel = styled.span`
  font-family: 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
`

const TotalAmount = styled.span`
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

const StyledModal = styled(PrimaryInfoModal)`
  max-width: 400px;
  .ant-modal-body {
    gap: 16px;
  }
`

export const ContentWrapper = styled.div`
  padding: 30px 24px 34px;
  color: ${({ theme }) => theme.color.darkGrey};
  line-height: 150%;
  > p {
    line-height: 150%;
    margin-bottom: 16px;
  }
`

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 21px;
`

export const GreenText = styled.span`
  color: ${({ theme }) => theme.color.brandGreen};
`

export const ManualMatchingModalFooter: FC<ManualMatchingModalFooterProps> = ({
  totalAmount,
  totalPlaylists,
  totalCurators,
  isBelowMinBudget,
  minBudget,
  disabled,
  loading,
  onContinue,
  onAddMoreCurators,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const formattedTotalAmount = numberToMoney(totalAmount)
  const playlistText = totalPlaylists === 1 ? 'playlist' : 'playlists'
  const curatorsText = Number(totalCurators) === 1 ? 'curator' : 'curators'
  const shouldShowMinBudget =
    Boolean(isBelowMinBudget) && typeof minBudget === 'number'

  const handleContinueClick = (): void => {
    if (totalCurators < MIN_CURATORS_THRESHOLD) {
      setIsModalOpen(true)
    } else {
      onContinue()
    }
  }

  const handleAddMoreCurators = (): void => {
    setIsModalOpen(false)
    onAddMoreCurators?.()
  }

  const handleContinueToCheckout = (): void => {
    setIsModalOpen(false)
    onContinue()
  }

  return (
    <Container>
      <Wrapper>
        <TotalSection>
          <TotalLabel>Total: ${formattedTotalAmount}</TotalLabel>
          <TotalAmount>
            {shouldShowMinBudget
              ? `$${minBudget} minimum required`
              : `${totalPlaylists} ${playlistText} by ${totalCurators} ${curatorsText}`}
          </TotalAmount>
        </TotalSection>
        <StyledCTAButton
          disabled={disabled}
          loading={loading}
          onClick={handleContinueClick}
        >
          Continue
        </StyledCTAButton>
      </Wrapper>
      <StyledModal
        isOpen={isModalOpen}
        title='Maximize your chances'
        onClose={handleAddMoreCurators}
      >
        <ContentWrapper>
          <p>
            Artists who select <GreenText>3 or more </GreenText>curators get
            significantly more feedback and higher chances of playlist
            placement. You currently have only {totalCurators} selected.
          </p>
          <ModalActions>
            <StyledCTAButton onClick={handleAddMoreCurators}>
              Add more curators
            </StyledCTAButton>
            <PrimaryDottedButton onClick={handleContinueToCheckout}>
              Continue to checkout
            </PrimaryDottedButton>
          </ModalActions>
        </ContentWrapper>
      </StyledModal>
    </Container>
  )
}
